import {period, range} from '@telia/cpa-web-common';
import {Button, Form} from '@telia/styleguide';
import React, {FC} from 'react';

import {Entity, useFormState} from '../../hooks/useFormState';
import {useModal} from '../../hooks/useModal';
import {useProductTypes} from '../../hooks/useProductTypes';
import {getLog} from '../../log';
import {ID, Product} from '../../model';
import FormColumn from '../common/FormColumn';
import FormRow from '../common/FormRow';
import {FieldTypes, FieldWithFormState} from '../common/field';
import {PickAccessNumber} from './PickAccessNumber';

const log = getLog('PendingUsers', 'INFO');

const {activeStatus, isValid} = period;

interface FeaturesProps {
  product: Product;
  id: string;
  saveCallback: (product: Product) => void;
}

export const FeatureForm: FC<FeaturesProps> = ({product, id, saveCallback}) => {
  const i = product.features?.findIndex((feature) => feature.id === id)!;
  const fieldPrefix = `features[${i}]`;
  const formState = useFormState({
    isEditing: true,
    initialEntity: product as unknown as Entity,
  });
  const {isEditing, onChange, entityAs} = formState;
  const entity = entityAs<Product>();
  const feature = entity.features ? entity.features[i] : undefined;

  log.debug('FeatureForm', product, feature);

  const {showModal} = useModal();

  const {getProductTypeFeatures} = useProductTypes();

  const onSaveFeature = async () => {
    const product = {
      ...entity,
    };
    log.debug('onSaveFeature', {product});

    saveCallback(product);
  };

  let modalId = '';
  const updateAccessNumberRange = (from?: ID, count?: number) => {
    const accessNumberRange = {from, to: !from || !count ? undefined : range.rangeTo(from, count), count};
    log.debug('updateAccessNumberRange ', accessNumberRange);
    onChange('accessNumberRange')(accessNumberRange);
  };

  const onPickAccessNumber = (accessNumberField: ID) => (accessNumber: ID) => {
    onChange(accessNumberField)(accessNumber);
    if (accessNumberField === 'accessNumberRange.from') {
      const count = entity.accessNumberRange?.count;
      updateAccessNumberRange(accessNumber, count);
    }

    // need to create an updated entity, since react has not yet triggered a useFormState update so old state still appears in entity
    const updatedEntity = {
      ...entity,
      features: entity.features?.map((feature) => {
        if (feature.id === id) {
          return {
            ...feature,
            featureProperties: {
              ...feature.featureProperties, // spreading this will future proof if we add more fields in featureProperties
              accessNumberId: accessNumber,
            },
          };
        }

        return feature;
      }),
    };

    const config = {
      title: `${id ? 'Edit' : 'Create'}  Feature`,
      content: <FeatureForm product={updatedEntity} id={id} saveCallback={saveCallback} />,
    };

    showModal(config);
  };

  const onPickButtonClick = (accessNumberField: ID) => () => {
    const config = {
      title: `Pick access number`,
      content: (
        <PickAccessNumber
          productType={entity.productType}
          productId={entity.id}
          featureType={entity.features?.[i]?.featureTypeId}
          featureId={entity.features?.[i]?.id}
          currentNumber={entity.features?.[i].featureProperties?.accessNumberId}
          rangeCount={entity.accessNumberRange?.count}
          startDate={entity.features?.[i]?.startDate || entity.startDate}
          endDate={entity.features?.[i]?.endDate || entity.endDate}
          onPickAccessNumber={onPickAccessNumber(accessNumberField)}
        />
      ),
    };

    modalId = showModal(config);
  };

  return (
    <div className="marginBottom2 form--compact">
      <Form>
        <>
          <FormRow>
            <FormColumn>
              <FieldWithFormState
                formState={formState}
                entityFieldId={`${fieldPrefix}.featureTypeId`}
                label="Feature Type"
                type={FieldTypes.select}
                options={getProductTypeFeatures(entity.productType)}
              />
            </FormColumn>
          </FormRow>
          {entity.features &&
            entity.features[i]?.featureTypeId /* This will need more validation when we add more featureTypes */ && (
              <FormRow>
                <FormColumn>
                  <FieldWithFormState
                    formState={formState}
                    entityFieldId={`${fieldPrefix}.featureProperties.accessNumberId`}
                    label="Access Number"
                    tip={!isValid(entity) && `Disabled because dates are ${activeStatus(entity)}.`}
                    button={
                      isEditing && (
                        <Button
                          text={'Pick'}
                          className="floatRight"
                          onClick={onPickButtonClick(`${fieldPrefix}.featureProperties.accessNumberId`)}
                          isDisabled={!isValid(entity)}
                        />
                      )
                    }
                    isDisabled={!isValid(entity)}
                  />
                  <div></div>
                </FormColumn>
              </FormRow>
            )}

          <FormRow>
            <FormColumn>
              <FieldWithFormState
                formState={formState}
                entityFieldId={`${fieldPrefix}.startDate`}
                label="Feature start date"
                type={FieldTypes.date}
                defaultValue={feature?.startDate}
                tip="Leave empty to inherit service's start date"
              />
              <FieldWithFormState
                formState={formState}
                entityFieldId={`${fieldPrefix}.endDate`}
                label="Feature end date"
                type={FieldTypes.date}
                defaultValue={feature?.endDate}
                tip="Leave empty to inherit service's end date"
              />
            </FormColumn>
          </FormRow>
          <br />
        </>
        <div>
          <Button text="Save" onClick={onSaveFeature} kind={Button.kinds.primary} />
        </div>
      </Form>
    </div>
  );
};
